import React, { useState, useEffect, useCallback } from "react";
import TablePageLayout from "../../../components/TablePageLayout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../shadcn/ui/table";
import { Badge } from "../../../shadcn/ui/badge";
import { Button } from "../../../shadcn/ui/button";
import { FaEllipsisH } from "react-icons/fa";
import { organizationApiService } from "../../../services/apiV2Service";
import { showApiMessage } from "../../../utils/messageHandler";
import logger from "../../../utils/logger";
import EditOrganizationModal from "../../host-experience/host-clients/components/modals/EditOrganizationModal";
import { useHostOrganizationEdit } from "../hooks/useHostOrganizationEdit";

const SuspendedHosts = () => {
  const [suspendedHosts, setSuspendedHosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPageSize, setTotalPageSize] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(true);

  // Edit functionality using shared hook
  const {
    showEditModal,
    setShowEditModal,
    selectedOrganization,
    editFormData,
    editLoading,
    editError,
    fetchingData,
    commissionPercentage,
    setCommissionPercentage,
    isCustomRevenueShare,
    defaultCommission,
    handleEditShow,
    handleEditClose,
    handleInputChange,
    handleSubmit,
    handleDeleteRevenueShare,
  } = useHostOrganizationEdit();

  useEffect(() => {
    loadOrganizationsByStatus();
  }, []);

  // Auto-search after 3 characters
  useEffect(() => {
    if (searchValue.length >= 3 || searchValue.length === 0) {
      setCurrentPage(1);
      loadOrganizationsByStatus(1, searchValue);
    }
  }, [searchValue]);

  const loadOrganizationsByStatus = useCallback(
    async (page = currentPage, search = searchValue) => {
      setLoading(true);
      try {
        // Get SUSPENDED organizations for both HOST_PROPERTY_DISTRIBUTION and HOST_PROPERTY_MANAGEMENT types
        const distributionParams = {
          typeId: 2, // HOST_PROPERTY_DISTRIBUTION
          status: "SUSPENDED",
          page: page,
          limit: 10,
          ...(search && { q: search }), // Add q param if search exists
        };

        const managementParams = {
          typeId: 3, // HOST_PROPERTY_MANAGEMENT
          status: "SUSPENDED",
          page: page,
          limit: 10,
          ...(search && { q: search }), // Add q param if search exists
        };

        const [distributionResponse, managementResponse] = await Promise.all([
          organizationApiService.getOrganizations(distributionParams),
          organizationApiService.getOrganizations(managementParams),
        ]);

        const distributionOrgs = distributionResponse.data?.data || [];
        const managementOrgs = managementResponse.data?.data || [];
        const allOrganizations = [...distributionOrgs, ...managementOrgs];

        const pagination = distributionResponse.data?.pagination || {};

        setTotalPageSize(pagination.totalCount || allOrganizations.length);
        setItemsPerPage(pagination.limit || 10);
        setSuspendedHosts(allOrganizations);
      } catch (err) {
        logger.error("Error loading suspended organizations:", err);
        showApiMessage(err, "fetch suspended organizations");
      } finally {
        setLoading(false);
      }
    },
    [currentPage]
  );

  const changePage = async selectedObj => {
    const pageNumber = Number(selectedObj.selected) || 1;
    setCurrentPage(pageNumber);
    // Reload data for the new page
    loadOrganizationsByStatus();
  };

  const performSearch = async () => {
    // Reset to first page when searching
    setCurrentPage(1);
    setLoading(true);

    try {
      // For now, we'll use the same loadOrganizationsByStatus function
      // Later this can be enhanced to include search functionality
      await loadOrganizationsByStatus();
    } catch (err) {
      logger.error("Error searching hosts:", err);
      showApiMessage(err, "search hosts");
    } finally {
      setLoading(false);
    }
  };

  return (
    <TablePageLayout
      title="Suspended Hosts"
      subtitle="Manage and view suspended hosts"
      breadcrumbs={[{ label: "Home", path: "/host" }, { label: "Suspended Hosts" }]}
      showSearch={true}
      searchValue={searchValue}
      onSearchChange={e => setSearchValue(e.target.value)}
      onSearchSubmit={performSearch}
      searchPlaceholder="Search hosts..."
      loading={loading}
      emptyMessage="No suspended hosts found"
      showPagination={Array.isArray(suspendedHosts) && suspendedHosts.length > 0 && totalPageSize > 0}
      currentPage={currentPage}
      totalCount={totalPageSize}
      pageSize={itemsPerPage}
      onPageChange={changePage}
    >
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-12 font-semibold">#</TableHead>
              <TableHead className="font-semibold">Company</TableHead>
              <TableHead className="font-semibold">Contact</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold">Date Suspended</TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {suspendedHosts.map((host, index) => (
              <TableRow key={host.id} className="hover:bg-muted/50">
                <TableCell className="text-gray-500 font-medium">
                  {(currentPage - 1) * itemsPerPage + index + 1}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium">{host.name}</span>
                    <span className="text-sm text-gray-600">{host.address || "N/A"}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="text-sm">{host.contactEmail || "N/A"}</span>
                    <span className="text-sm text-gray-600">{host.contactMsisdn || "N/A"}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
                    SUSPENDED
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <div className="font-medium">{new Date(host.createdAt).toLocaleDateString()}</div>
                    <div className="text-xs text-muted-foreground">{new Date(host.createdAt).toLocaleTimeString()}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button variant="ghost" size="sm" onClick={() => handleEditShow(host)} title="Edit Host">
                      <FaEllipsisH className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit Host Dialog */}
      <EditOrganizationModal
        isOpen={showEditModal}
        onClose={handleEditClose}
        selectedOrganization={selectedOrganization}
        editOrganizationForm={editFormData}
        editOrganizationLoading={editLoading}
        editOrganizationError={editError}
        fetchingOrganizationData={fetchingData}
        onInputChange={handleInputChange}
        onSubmit={async () => {
          const result = await handleSubmit();
          if (result?.success) {
            loadOrganizationsByStatus();
          }
        }}
        isHostMode={true}
        commissionPercentage={commissionPercentage}
        onCommissionChange={setCommissionPercentage}
        defaultCommission={defaultCommission}
        isCustomRevenueShare={isCustomRevenueShare}
        onDeleteRevenueShare={handleDeleteRevenueShare}
      />
    </TablePageLayout>
  );
};

export default SuspendedHosts;
